import { useMemo, useState } from "react";
import { compareInDirection } from "@/lib/table-sort";
import { usePersistedSort, type SortDir } from "@/lib/use-persisted-sort";

export type StatusFilter = "active" | "inactive" | "all";
export type { SortDir };

export interface UseTableControlsOptions<T> {
    /** Returns the active flag for a row; enables the status filter when set. */
    getActive?: (row: T) => boolean;
    /** Initial status filter; defaults to "active". */
    defaultStatus?: StatusFilter;
    /** Optional external search string to filter against. */
    searchText?: string;
    /** Text extracted from a row to match against `searchText`. */
    searchAccessor?: (row: T) => string;
    /** Map of sort key → value accessor for that column. */
    sortAccessors: Record<string, (row: T) => string | number | null | undefined>;
    /** Initial sort key; null preserves the input order until a header is clicked. */
    defaultSortKey?: string | null;
    /** Initial sort direction; defaults to "asc". */
    defaultSortDir?: SortDir;
    /** Names the table so a clicked sort is remembered for this user (see use-persisted-sort). */
    sortId?: string;
    /** Columns whose first click sorts descending (money, dates, counts). */
    descFirst?: readonly string[];
}

export interface UseTableControlsResult<T> {
    visible: T[];
    status: StatusFilter;
    setStatus: (s: StatusFilter) => void;
    sortKey: string | null;
    sortDir: SortDir;
    toggleSort: (key: string) => void;
    /** False until a saved sort has been read (always true without `sortId`). */
    sortLoaded: boolean;
}

/**
 * Shared status-filter + search + sort logic for hand-rolled admin tables
 * (the same pattern Slice 5 introduced on the water-providers / reporting-
 * platforms pages). Filters by status (when `getActive` provided), then by
 * `searchText` (when both `searchText` and `searchAccessor` provided), then
 * sorts by the active sort accessor.
 */
export function useTableControls<T>(rows: T[], opts: UseTableControlsOptions<T>): UseTableControlsResult<T> {
    const {
        getActive,
        defaultStatus = "active",
        searchText,
        searchAccessor,
        sortAccessors,
        defaultSortKey = null,
        defaultSortDir = "asc",
        sortId,
        descFirst,
    } = opts;

    const [status, setStatus] = useState<StatusFilter>(defaultStatus);
    const sort = usePersistedSort({
        tableId: sortId,
        defaultKey: defaultSortKey,
        defaultDir: defaultSortDir,
        validKeys: Object.keys(sortAccessors),
    });
    const { sortKey, sortDir } = sort;
    const toggleSort = (key: string) => sort.toggleSort(key, descFirst?.includes(key) ? "desc" : "asc");

    const visible = useMemo(() => {
        let result = rows;

        if (getActive) {
            if (status === "active") result = result.filter((r) => getActive(r));
            else if (status === "inactive") result = result.filter((r) => !getActive(r));
        }

        const q = searchText?.trim().toLowerCase();
        if (q && searchAccessor) {
            result = result.filter((r) => searchAccessor(r).toLowerCase().includes(q));
        }

        if (sortKey && sortAccessors[sortKey]) {
            const accessor = sortAccessors[sortKey];
            result = [...result].sort((a, b) => compareInDirection(accessor(a), accessor(b), sortDir));
        }

        return result;
    }, [rows, getActive, status, searchText, searchAccessor, sortAccessors, sortKey, sortDir]);

    return { visible, status, setStatus, sortKey, sortDir, toggleSort, sortLoaded: sort.loaded };
}
